import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslationApp } from "../../../../lib/i18next";
import { MODAL_COMPETITORS_CHANGE_LOCATION } from "../../../../i18n/keysTranslations";

const TOAST_AUTO_DISMISS_MS = 10_000;

export const useCompetitorFavoriteToast = ({ modalOpen = false }) => {
  const { t } = useTranslationApp();
  const [toastState, setToastState] = useState(null);
  const dismissTimeoutRef = useRef(null);

  const clearDismissTimeout = useCallback(() => {
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }
  }, []);

  const resetToast = useCallback(() => {
    clearDismissTimeout();
    setToastState(null);
  }, [clearDismissTimeout]);

  const dismissFavoriteToast = useCallback(() => {
    clearDismissTimeout();
    setToastState((current) =>
      current ? { ...current, isVisible: false } : null
    );
  }, [clearDismissTimeout]);

  useEffect(() => {
    if (!modalOpen) {
      resetToast();
    }
  }, [modalOpen, resetToast]);

  useEffect(() => () => clearDismissTimeout(), [clearDismissTimeout]);

  const onFadeExited = useCallback(() => {
    setToastState(null);
  }, []);

  const showFavoriteToast = useCallback(
    ({
      mediaUrl = "",
      mediaType = "image",
      messageKey,
      messageParams = {},
      showChangeLocation = false,
      onChangeLocation = () => {},
    }) => {
      if (!messageKey) return;

      clearDismissTimeout();

      setToastState({
        mediaUrl,
        mediaType,
        messageKey,
        messageParams,
        showChangeLocation,
        onChangeLocation,
        toastKey: Date.now(),
        isVisible: true,
      });

      dismissTimeoutRef.current = setTimeout(() => {
        setToastState((current) =>
          current ? { ...current, isVisible: false } : null
        );
        dismissTimeoutRef.current = null;
      }, TOAST_AUTO_DISMISS_MS);
    },
    [clearDismissTimeout]
  );

  const favoriteToastProps = toastState
    ? {
        open: toastState.isVisible,
        mediaUrl: toastState.mediaUrl,
        mediaType: toastState.mediaType,
        message: t(toastState.messageKey, toastState.messageParams),
        toastKey: toastState.toastKey,
        showChangeLocation: toastState.showChangeLocation,
        changeLocationLabel: t(MODAL_COMPETITORS_CHANGE_LOCATION),
        onChangeLocation: toastState.onChangeLocation,
        onDismiss: dismissFavoriteToast,
        onExited: onFadeExited,
      }
    : null;

  return {
    showFavoriteToast,
    dismissFavoriteToast,
    favoriteToastProps,
  };
};

export default useCompetitorFavoriteToast;
